// Crypto Wallets → Configuration → Withdrawal Policies (SM-278).
// One policy per asset: the limits and fee applied when users withdraw
// it. Whether withdrawals are POSSIBLE at all is decided on Deposit &
// Withdrawal (SM-277, SM-504) — the list shows that state read-only and
// never resurrects the policy row's legacy enabled flag. An asset
// without a policy withdraws with the default: no limits, zero fee.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import {
  errMsg,
  td,
  th,
  useCanConfig,
} from "@/components/wallet-ops/configShared";
import {
  CoverageLine,
  PolicyLoadError,
  PolicyPageHeader,
  RuleAmount,
  UncoveredCallout,
} from "@/components/wallet-ops/policyShared";
import {
  createWithdrawalConfig,
  fetchCryptoAssets,
  fetchWithdrawalConfigs,
  putWithdrawalConfig,
  walletKeys,
} from "@/lib/wallet-queries";
import type { CryptoAsset, CryptoWithdrawalConfig } from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute(
  "/_app/wallet-ops/config/policies/withdrawal",
)({
  component: WithdrawalPoliciesPage,
});

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; cfg: CryptoWithdrawalConfig }
  | null;

function WithdrawalPoliciesPage() {
  const canConfig = useCanConfig();
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const configs = useQuery({
    queryKey: walletKeys.withdrawalConfigs(),
    queryFn: fetchWithdrawalConfigs,
  });
  const [editor, setEditor] = useState<EditorState>(null);

  const allAssets = assets.data ?? [];
  const rows = configs.data ?? [];
  const covered = new Set(rows.map((c) => c.asset_id));
  const assetByID = (id: string) => allAssets.find((a) => a.asset_id === id);

  const creatable = allAssets.filter((a) => !covered.has(a.asset_id));
  const uncoveredWithdrawable = allAssets.filter(
    (a) => a.withdrawal_enabled && !covered.has(a.asset_id),
  );
  const withdrawableCovered = allAssets.filter(
    (a) => a.withdrawal_enabled && covered.has(a.asset_id),
  ).length;

  return (
    <div className="space-y-6">
      <PolicyPageHeader
        title="Withdrawal policies"
        definition={
          <>
            The limits and fee applied when users withdraw an asset. One
            policy per asset; an asset without a policy withdraws with no
            limits and zero fee. Whether users can withdraw at all is decided
            on{" "}
            <Link
              to="/wallet-ops/config/rails"
              className="underline underline-offset-2 hover:text-t-1"
            >
              Deposit &amp; Withdrawal
            </Link>
            .
          </>
        }
        steps={[
          "user requests a withdrawal",
          "amount inside min / max / daily cap?",
          "fee added per the policy",
          "sent on-chain",
        ]}
      />

      <div className="space-y-3">
        <CoverageLine
          covered={withdrawableCovered}
          total={allAssets.filter((a) => a.withdrawal_enabled).length}
          noun="withdrawable asset"
        />
        <UncoveredCallout
          tone="info"
          names={uncoveredWithdrawable.map((a) => a.symbol)}
          consequence="withdrawals of these assets run on the default policy — no limits, zero fee. Add a policy to bound or price them."
        />
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Policies</CardTitle>
            {canConfig && (
              <Button size="sm" onClick={() => setEditor({ mode: "create" })}>
                New policy
              </Button>
            )}
          </CardHeader>
          <CardBody className="p-0">
            {configs.isLoading ? (
              <div className="space-y-2 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : configs.isError ? (
              <PolicyLoadError
                message={errMsg(configs.error)}
                onRetry={() => void configs.refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                className="m-5"
                title="No withdrawal policies yet"
                description="Every withdrawable asset currently uses the default policy: no limits and zero fee. Create a policy to set minimums, caps, or a fee."
                action={
                  canConfig && (
                    <Button
                      size="sm"
                      onClick={() => setEditor({ mode: "create" })}
                    >
                      New policy
                    </Button>
                  )
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-2">
                    <tr>
                      <th className={th}>Asset</th>
                      <th className={th}>Limits</th>
                      <th className={th}>Fee</th>
                      <th className={th}>Withdrawals</th>
                      <th className={th}>Updated</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => {
                      const asset = assetByID(c.asset_id);
                      return (
                        <tr
                          key={c.asset_id}
                          className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2"
                          onClick={() => setEditor({ mode: "edit", cfg: c })}
                        >
                          <td className={td}>
                            <div className="font-medium text-t-1">
                              {asset?.symbol ?? c.asset_id}
                            </div>
                            <div className="font-mono text-2xs text-muted">
                              {c.asset_id}
                            </div>
                          </td>
                          <td className={td}>
                            <LimitsCell cfg={c} asset={asset} />
                          </td>
                          <td className={td}>
                            <FeeCell cfg={c} asset={asset} />
                          </td>
                          <td className={td}>
                            {asset ? (
                              asset.withdrawal_enabled ? (
                                <Badge tone="success">On</Badge>
                              ) : (
                                <Badge tone="muted">Off</Badge>
                              )
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className={`${td} text-muted`}>
                            {c.updated_at ? fmtRelative(c.updated_at) : "—"}
                          </td>
                          <td className={`${td} text-right`}>
                            {canConfig && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditor({ mode: "edit", cfg: c });
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
        <p className="text-2xs text-muted">
          Withdrawals is the asset's own on/off switch from Assets &amp;
          Rates, shown here for context — a policy only sets limits and fees,
          it can't turn withdrawals off.
        </p>
      </div>

      <WithdrawalPolicyEditor
        state={editor}
        creatable={creatable}
        assetByID={assetByID}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}

function LimitsCell({
  cfg,
  asset,
}: {
  cfg: CryptoWithdrawalConfig;
  asset: CryptoAsset | undefined;
}) {
  const dec = asset?.token_decimals ?? 18;
  if (!cfg.min_base && !cfg.max_base && !cfg.daily_max_base) {
    return <span className="text-muted">no limits</span>;
  }
  return (
    <span className="text-t-2">
      {cfg.min_base && (
        <>
          min{" "}
          <RuleAmount base={cfg.min_base} decimals={dec} symbol={asset?.symbol} />
        </>
      )}
      {cfg.max_base && (
        <>
          {cfg.min_base ? " · " : ""}max{" "}
          <RuleAmount base={cfg.max_base} decimals={dec} symbol={asset?.symbol} />
        </>
      )}
      {cfg.daily_max_base && (
        <>
          {cfg.min_base || cfg.max_base ? " · " : ""}daily{" "}
          <RuleAmount
            base={cfg.daily_max_base}
            decimals={dec}
            symbol={asset?.symbol}
          />
        </>
      )}
    </span>
  );
}

function FeeCell({
  cfg,
  asset,
}: {
  cfg: CryptoWithdrawalConfig;
  asset: CryptoAsset | undefined;
}) {
  const dec = asset?.token_decimals ?? 18;
  if (cfg.fee_mode === "PCT") {
    return (
      <span className="text-t-2">
        {cfg.fee_value} bps ({(cfg.fee_value / 100).toFixed(2)}%)
      </span>
    );
  }
  if (cfg.fee_mode === "GAS_PASSTHROUGH") {
    return <span className="text-t-2">network gas at execution</span>;
  }
  if (!cfg.fee_value) return <span className="text-muted">no fee</span>;
  return (
    <span className="text-t-2">
      <RuleAmount
        base={String(cfg.fee_value)}
        decimals={dec}
        symbol={asset?.symbol}
      />{" "}
      flat
    </span>
  );
}

function WithdrawalPolicyEditor({
  state,
  creatable,
  assetByID,
  onClose,
}: {
  state: EditorState;
  creatable: CryptoAsset[];
  assetByID: (id: string) => CryptoAsset | undefined;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = state !== null;
  const editing = state?.mode === "edit" ? state.cfg : null;
  const [draft, setDraft] = useState<CryptoWithdrawalConfig>(
    blankWithdrawalConfig(),
  );
  const draftKey =
    state === null ? "closed" : state.mode === "create" ? "create" : state.cfg.asset_id;
  const [lastKey, setLastKey] = useState("closed");
  if (draftKey !== lastKey) {
    setLastKey(draftKey);
    setDraft(editing ? { ...editing } : blankWithdrawalConfig());
  }

  const save = useMutation({
    mutationFn: (input: CryptoWithdrawalConfig) =>
      editing ? putWithdrawalConfig(input) : createWithdrawalConfig(input),
    onSuccess: async () => {
      onClose();
      await qc.invalidateQueries({ queryKey: walletKeys.withdrawalConfigs() });
      await qc.invalidateQueries({
        queryKey: walletKeys.withdrawalConfig(draft.asset_id),
      });
    },
    meta: {
      successMessage: editing
        ? "Withdrawal policy updated."
        : "Withdrawal policy created.",
    },
  });

  const set = <K extends keyof CryptoWithdrawalConfig>(
    k: K,
    v: CryptoWithdrawalConfig[K],
  ) => setDraft((d) => ({ ...d, [k]: v }));

  const asset = assetByID(draft.asset_id);
  const dec = asset?.token_decimals ?? 18;
  const canSubmit = draft.asset_id !== "";

  return (
    <FormModal
      open={open}
      title={
        editing
          ? `Edit withdrawal policy — ${assetByID(editing.asset_id)?.symbol ?? editing.asset_id}`
          : "New withdrawal policy"
      }
      description={
        editing
          ? "Changes apply to the next withdrawal request."
          : "Picks are limited to assets without a policy — each asset has at most one."
      }
      confirmLabel={editing ? "Save changes" : "Create policy"}
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={canSubmit}
      onSubmit={() => save.mutate(draft)}
      onCancel={() => {
        onClose();
        save.reset();
      }}
    >
      <div className="space-y-4">
        {editing ? (
          <Input
            label="Asset"
            value={`${assetByID(editing.asset_id)?.symbol ?? editing.asset_id} (${editing.asset_id})`}
            disabled
            hint="A policy stays with its asset — create a separate policy for another one."
          />
        ) : (
          <Select
            label="Asset"
            placeholder="Pick an asset"
            value={draft.asset_id}
            onChange={(e) => set("asset_id", e.target.value)}
            options={creatable.map((a) => ({
              value: a.asset_id,
              label: `${a.symbol} · ${a.asset_id}`,
            }))}
            hint={
              creatable.length === 0
                ? "Every asset already has a policy — edit one from the list instead."
                : "Assets that already have a policy aren't listed."
            }
          />
        )}
        <TokenAmountInput
          label="Minimum per withdrawal"
          symbol={asset?.symbol}
          base={draft.min_base}
          decimals={dec}
          onBase={(b) => set("min_base", b)}
          allowEmpty
          emptyHint="empty = no minimum"
        />
        <TokenAmountInput
          label="Maximum per withdrawal"
          symbol={asset?.symbol}
          base={draft.max_base}
          decimals={dec}
          onBase={(b) => set("max_base", b)}
          allowEmpty
          emptyHint="empty = no maximum"
        />
        <TokenAmountInput
          label="Daily cap per user"
          symbol={asset?.symbol}
          base={draft.daily_max_base}
          decimals={dec}
          onBase={(b) => set("daily_max_base", b)}
          allowEmpty
          emptyHint="empty = no daily cap"
        />
        <Select
          label="Fee charged"
          value={draft.fee_mode}
          onChange={(e) => set("fee_mode", e.target.value)}
          options={[
            { value: "FLAT", label: "Flat token amount" },
            { value: "PCT", label: "Percent of the amount" },
            { value: "GAS_PASSTHROUGH", label: "Actual network gas" },
          ]}
        />
        {draft.fee_mode === "FLAT" && (
          <TokenAmountInput
            label="Fee"
            symbol={asset?.symbol}
            base={draft.fee_value ? String(draft.fee_value) : ""}
            decimals={dec}
            onBase={(b) => {
              const n = Number(b || 0);
              if (Number.isSafeInteger(n)) set("fee_value", n);
            }}
            allowEmpty
            emptyHint="empty = no fee"
          />
        )}
        {draft.fee_mode === "PCT" && (
          <Input
            label="Fee (basis points)"
            value={String(draft.fee_value)}
            onChange={(e) => set("fee_value", Number(e.target.value) || 0)}
            inputMode="numeric"
            hint={`${draft.fee_value} bps = ${(draft.fee_value / 100).toFixed(2)}% of the withdrawal`}
          />
        )}
        {draft.fee_mode === "GAS_PASSTHROUGH" && (
          <p className="text-2xs text-muted">
            The fee is whatever the network charges when the withdrawal
            executes — nothing to configure.
          </p>
        )}
        <Input
          label="Amount precision (decimal places)"
          value={String(draft.precision_dp)}
          onChange={(e) => set("precision_dp", Number(e.target.value) || 0)}
          inputMode="numeric"
          hint="How many decimals users may enter for withdrawal amounts."
        />
        <p className="text-2xs text-muted">
          To stop withdrawals of this asset entirely, use its switch on
          Deposit &amp; Withdrawal — a policy only bounds and prices them.
        </p>
      </div>
    </FormModal>
  );
}

function blankWithdrawalConfig(): CryptoWithdrawalConfig {
  // `enabled` is the legacy pre-SM-277 flag the rail ignores — always
  // write true so stored rows never contradict the real switch (the
  // asset's withdrawal_enabled).
  return {
    asset_id: "",
    min_base: "",
    max_base: "",
    daily_max_base: "",
    fee_mode: "FLAT",
    fee_value: 0,
    precision_dp: 0,
    enabled: true,
  };
}
